import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { trackEvent } from '../../../utils/analytics';
import './EventForm.css';

/**
 * EventForm - Reusable event creation/editing form
 * 
 * Can be used in standalone pages or within modals
 * Supports both create and edit modes
 */
export default function EventForm({
  prefill = {},
  compact = false,
  onCancel,
  onSuccess,
  onDirtyChange,
  isEditMode = false,
  eventId = null,
  renderActions = true
}) {
  const { user } = useAuth();
  const { getEndeavorsByUser, createEvent, updateEvent, getEvent } = useData();

  const initialDate = prefill.selectedDate ? new Date(prefill.selectedDate) : new Date();
  const defaultEventType = prefill.defaultEventType || 'lecture';
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    eventDate: initialDate.toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '10:00',
    eventType: defaultEventType,
    endeavorId: prefill.endeavorId || '',
    location: '',
    isRecurring: false,
    recurringPattern: 'weekly',
    recurringEndDate: '',
    recurringDaysOfWeek: [] // 0-6 (Sun=0, Mon=1, ..., Sat=6)
  });

  const [endeavors, setEndeavors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [, setOriginalEvent] = useState(null);

  // Store initial snapshot for dirty tracking
  const initialSnapshot = useMemo(() => JSON.stringify(formData), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track dirty state
  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== initialSnapshot;
  }, [formData, initialSnapshot]);

  // Notify parent of dirty state changes
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const eventTypes = useMemo(() => [
    { value: 'lecture', label: '🎓 Lecture', icon: '🎓' },
    { value: 'lab', label: '🔬 Lab', icon: '🔬' },
    { value: 'discussion', label: '💬 Discussion', icon: '💬' },
    { value: 'exam', label: '📝 Exam', icon: '📝' },
    { value: 'quiz', label: '❓ Quiz', icon: '❓' },
    { value: 'assignment', label: '📋 Assignment', icon: '📋' },
    { value: 'office_hours', label: '🏢 Office Hours', icon: '🏢' },
    { value: 'meeting', label: '👥 Meeting', icon: '👥' },
    { value: 'workshop', label: '🔧 Workshop', icon: '🔧' },
    { value: 'presentation', label: '📊 Presentation', icon: '📊' },
    { value: 'field_trip', label: '🚌 Field Trip', icon: '🚌' },
    { value: 'guest_speaker', label: '🎤 Guest Speaker', icon: '🎤' },
    { value: 'other', label: '📅 Other', icon: '📅' },
    { value: 'personal', label: '🙋 Personal', icon: '🙋' },
    { value: 'social', label: '🎉 Social', icon: '🎉' },
    { value: 'work', label: '💼 Work', icon: '💼' },
    { value: 'appointment', label: '🩺 Appointment', icon: '🩺' },
    { value: 'reminder', label: '🔔 Reminder', icon: '🔔' }
  ], []);

  // Load event data for edit mode
  const loadEventData = useCallback(async () => {
    if (!eventId) return;
    
    setLoadingEvent(true);
    try {
      const eventData = await getEvent(eventId); // FIXED: was getEventById
      setOriginalEvent(eventData);
      
      const startDate = new Date(eventData.startDatetime || eventData.eventDate);
      const endDate = new Date(eventData.endDatetime || eventData.startDatetime || eventData.eventDate);
      
      // Parse RRULE if present
      const parseRecurrenceRule = (rrule) => {
        if (!rrule) return { pattern: 'weekly', daysOfWeek: [], endDate: '' };
        
        const dayMap = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
        const parts = Object.fromEntries(
          rrule.split(';').map(s => {
            const [k, v] = s.split('=');
            return [k?.toUpperCase?.(), v];
          })
        );
        
        let pattern = 'weekly';
        let daysOfWeek = [];
        let endDate = '';
        
        // Check for weekdays pattern
        if (parts.BYDAY === 'MO,TU,WE,TH,FR') {
          pattern = 'weekdays';
          daysOfWeek = [1, 2, 3, 4, 5];
        } else if (parts.BYDAY) {
          // Parse BYDAY into numeric array
          const dayTokens = parts.BYDAY.split(',').map(d => d.trim().toUpperCase());
          daysOfWeek = dayTokens.map(d => dayMap[d]).filter(n => n !== undefined);
          pattern = daysOfWeek.length > 0 ? 'custom' : 'weekly';
        } else if (parts.FREQ === 'DAILY') {
          pattern = 'daily';
        }
        
        // Parse UNTIL date
        if (parts.UNTIL) {
          const until = parts.UNTIL;
          // Format: YYYYMMDD -> YYYY-MM-DD
          if (until.length === 8) {
            endDate = `${until.slice(0, 4)}-${until.slice(4, 6)}-${until.slice(6, 8)}`;
          }
        }
        
        return { pattern, daysOfWeek, endDate };
      };
      
      const recurData = eventData.recurrenceRule 
        ? parseRecurrenceRule(eventData.recurrenceRule) 
        : { pattern: 'weekly', daysOfWeek: [], endDate: '' };
      
      setFormData({
        name: eventData.name || '',
        description: eventData.description || '',
        eventDate: startDate.toISOString().split('T')[0],
        startTime: startDate.toTimeString().slice(0, 5),
        endTime: endDate.toTimeString().slice(0, 5),
        eventType: eventData.eventType || eventData.eventTypeName || 'lecture',
        endeavorId: eventData.endeavorId || '',
        location: eventData.location || '',
        isRecurring: eventData.isRecurring || false,
        recurringPattern: recurData.pattern,
        recurringEndDate: recurData.endDate,
        recurringDaysOfWeek: recurData.daysOfWeek
      });
    } catch (error) {
      console.error('Error loading event:', error);
      setError('Failed to load event data. Please try again.');
    } finally {
      setLoadingEvent(false);
    }
  }, [eventId, getEvent]);

  // Load endeavors
  const loadEndeavors = useCallback(async () => {
    try {
      const userId = user?.id || user?.userId;
      if (!userId) return;

      const data = await getEndeavorsByUser(userId);
      const userEndeavors = Array.isArray(data) ? data : (data.content || []);
      setEndeavors(userEndeavors);
      
      // Auto-select prefilled endeavor if provided; otherwise leave empty (personal event)
      if (!formData.endeavorId && !isEditMode && prefill.endeavorId) {
        setFormData(prev => ({
          ...prev,
          endeavorId: prefill.endeavorId
        }));
      }
    } catch (error) {
      console.error('Error loading endeavors:', error);
      setError('Failed to load courses. Please try again.');
    }
  }, [user, getEndeavorsByUser, formData.endeavorId, isEditMode]);

  // Initialize
  useEffect(() => {
    (async () => {
      try {
        await loadEndeavors();
        if (isEditMode) {
          await loadEventData();
        }
      } catch (err) {
        console.error('Initialization error in EventForm:', err);
        setError('Failed to load initial data for the form.');
      }
    })();
  }, [loadEndeavors, loadEventData, isEditMode]);

  // Helper: get day of week from date string (0=Sun, 1=Mon, ..., 6=Sat)
  const getDayOfWeek = useCallback((dateStr) => {
    return new Date(dateStr + 'T00:00:00').getDay();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for isRecurring checkbox
    if (name === 'isRecurring' && type === 'checkbox') {
      setFormData(prev => {
        const newData = { ...prev, [name]: checked };
        
        // When enabling recurring, initialize days of week for weekly pattern
        if (checked && prev.recurringPattern === 'weekly' && (!prev.recurringDaysOfWeek || prev.recurringDaysOfWeek.length === 0)) {
          const dow = getDayOfWeek(prev.eventDate);
          newData.recurringDaysOfWeek = [dow];
        }
        
        // When enabling recurring with weekdays pattern, set Mon-Fri
        if (checked && prev.recurringPattern === 'weekdays') {
          newData.recurringDaysOfWeek = [1, 2, 3, 4, 5];
        }
        
        return newData;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle recurring pattern change
  const handlePatternChange = useCallback((pattern) => {
    setFormData(prev => {
      const dow = getDayOfWeek(prev.eventDate);
      
      if (pattern === 'weekdays') {
        return { ...prev, recurringPattern: 'weekdays', recurringDaysOfWeek: [1, 2, 3, 4, 5] };
      }
      
      if (pattern === 'weekly') {
        return { 
          ...prev, 
          recurringPattern: 'weekly', 
          recurringDaysOfWeek: prev.recurringDaysOfWeek?.length ? prev.recurringDaysOfWeek : [dow] 
        };
      }
      
      if (pattern === 'custom') {
        return { 
          ...prev, 
          recurringPattern: 'custom', 
          recurringDaysOfWeek: prev.recurringDaysOfWeek?.length ? prev.recurringDaysOfWeek : [dow] 
        };
      }
      
      // daily
      return { ...prev, recurringPattern: 'daily', recurringDaysOfWeek: [] };
    });
  }, [getDayOfWeek]);

  // Toggle day of week selection
  const toggleDayOfWeek = useCallback((dow) => {
    setFormData(prev => {
      const set = new Set(prev.recurringDaysOfWeek || []);
      if (set.has(dow)) {
        set.delete(dow);
      } else {
        set.add(dow);
      }
      return { ...prev, recurringDaysOfWeek: Array.from(set).sort() };
    });
  }, []);

  // Sync days of week when event date changes and pattern is weekly/custom with no days
  useEffect(() => {
    if (formData.isRecurring && (formData.recurringPattern === 'weekly' || formData.recurringPattern === 'custom')) {
      if (!formData.recurringDaysOfWeek || formData.recurringDaysOfWeek.length === 0) {
        const dow = getDayOfWeek(formData.eventDate);
        setFormData(prev => ({ ...prev, recurringDaysOfWeek: [dow] }));
      }
    }
  }, [formData.eventDate, formData.isRecurring, formData.recurringPattern, formData.recurringDaysOfWeek, getDayOfWeek]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Manual validation: check end time > start time on same date
      if (formData.endTime <= formData.startTime) {
        setError('End time must be after start time.');
        setLoading(false);
        return;
      }

      // Validate recurring days of week
      if (formData.isRecurring && (formData.recurringPattern === 'custom' || formData.recurringPattern === 'weekly')) {
        if (!formData.recurringDaysOfWeek || formData.recurringDaysOfWeek.length === 0) {
          setError('Select at least one day for the recurring event.');
          setLoading(false);
          return;
        }
      }

      const startDatetime = `${formData.eventDate}T${formData.startTime}:00`;
      const endDatetime = `${formData.eventDate}T${formData.endTime}:00`;

      // Generate RRULE string for recurring events
      const generateRecurrenceRule = () => {
        if (!formData.isRecurring) return null;
        
        // Map numeric days (0-6, Sun-Sat) to RRULE day codes
        const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
        
        // Build RRULE components
        const parts = [];
        
        // Frequency based on pattern
        if (formData.recurringPattern === 'daily') {
          parts.push('FREQ=DAILY');
        } else if (formData.recurringPattern === 'weekly' || formData.recurringPattern === 'custom') {
          parts.push('FREQ=WEEKLY');
          if (formData.recurringDaysOfWeek && formData.recurringDaysOfWeek.length > 0) {
            const bydays = formData.recurringDaysOfWeek.map(dow => dayMap[dow]).join(',');
            parts.push(`BYDAY=${bydays}`);
            console.log('[EventForm] Converting days:', formData.recurringDaysOfWeek, '→', bydays);
          }
        } else if (formData.recurringPattern === 'weekdays') {
          parts.push('FREQ=WEEKLY');
          parts.push('BYDAY=MO,TU,WE,TH,FR');
        }
        
        // Add end date if specified
        if (formData.recurringEndDate) {
          const endDate = new Date(formData.recurringEndDate);
          const utcEndDate = endDate.toISOString().split('T')[0].replace(/-/g, '');
          parts.push(`UNTIL=${utcEndDate}`);
        }
        
        // Add week start (Sunday)
        if (formData.recurringPattern === 'weekly' || formData.recurringPattern === 'weekdays' || formData.recurringPattern === 'custom') {
          parts.push('WKST=SU');
        }
        
        const rrule = parts.join(';');
        console.log('[EventForm] Generated RRULE:', rrule);
        return rrule;
      };

      const eventData = {
        name: formData.name,
        description: formData.description,
        startDatetime: startDatetime,
        endDatetime: endDatetime,
        eventType: formData.eventType,
        endeavorId: formData.endeavorId || null,
        location: formData.location,
        isRecurring: formData.isRecurring,
        recurrenceRule: generateRecurrenceRule()
      };

      console.log('[EventForm] Submitting event:', eventData);

      let created;
      if (isEditMode) {
        created = await updateEvent(eventId, eventData);
      } else {
        created = await createEvent(eventData);
      }

      // Normalize ID
      const createdEventId = created?.eventId ?? created?.id ?? null;
      if (!createdEventId) {
        console.warn('[EventForm] missing eventId in create response', created);
      }

      const normalized = { ...created, _normalizedId: createdEventId };

      // Track success
      trackEvent?.('calendar_modal_create_success', { type: 'event' });

      // Call success handler instead of navigating
      if (onSuccess) {
        onSuccess(normalized);
      }
    } catch (error) {
      console.error('Error saving event:', error);
      setError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} event. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingEvent) {
    return (
      <div className={`event-form-loading ${compact ? 'compact' : ''}`}>
        <div className="loading-spinner"></div>
        <p>Loading event data...</p>
      </div>
    );
  }

  // No longer block event creation when no courses exist — users can create personal events

  return (
    <form 
      id="event-form"
      onSubmit={handleSubmit} 
      className={`event-form ${compact ? 'compact' : ''}`}
    >
      {error && (
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="form-section">
        <h3>📝 Event Details</h3>
        
        <div className="form-group">
          <label htmlFor="name">Event Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            placeholder="Enter event name..."
            autoFocus={compact && !isEditMode}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
            placeholder="Enter event description..."
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="eventType">Event Type *</label>
            <select
              id="eventType"
              name="eventType"
              value={formData.eventType}
              onChange={handleInputChange}
              required
            >
              {eventTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="endeavorId">Course</label>
            <select
              id="endeavorId"
              name="endeavorId"
              value={formData.endeavorId}
              onChange={handleInputChange}
            >
              <option value="">🚫 No course (personal event)</option>
              {endeavors.map(endeavor => (
                <option 
                  key={endeavor.endeavorId || endeavor.id} 
                  value={endeavor.endeavorId || endeavor.id}
                >
                  {endeavor.name}
                </option>
              ))}
            </select>
            {!formData.endeavorId && (
              <span className="field-hint">Personal events won't appear on your study timeline</span>
            )}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>📅 Date & Time</h3>
        
        <div className="form-row-three">
          <div className="form-group">
            <label htmlFor="eventDate">Date *</label>
            <input
              type="date"
              id="eventDate"
              name="eventDate"
              value={formData.eventDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="startTime">Start Time *</label>
            <input
              type="time"
              id="startTime"
              name="startTime"
              value={formData.startTime}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="endTime">End Time *</label>
            <input
              type="time"
              id="endTime"
              name="endTime"
              value={formData.endTime}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>📍 Additional Details</h3>
        
        <div className="form-group">
          <label htmlFor="location">Location</label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder="Enter location..."
          />
        </div>

        <div className="checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isRecurring"
              checked={formData.isRecurring}
              onChange={handleInputChange}
            />
            <span className="checkbox-custom"></span>
            Make this a recurring event
          </label>
        </div>

        {formData.isRecurring && (
          <div className="recurring-options">
            <div className="form-group">
              <label>Repeat Pattern</label>
              <div className="recurrence-pattern-row">
                <button
                  type="button"
                  className={`patternPill ${formData.recurringPattern === 'daily' ? 'isActive' : ''}`}
                  onClick={() => handlePatternChange('daily')}
                  aria-pressed={formData.recurringPattern === 'daily'}
                >
                  Daily
                </button>
                <button
                  type="button"
                  className={`patternPill ${formData.recurringPattern === 'weekly' ? 'isActive' : ''}`}
                  onClick={() => handlePatternChange('weekly')}
                  aria-pressed={formData.recurringPattern === 'weekly'}
                >
                  Weekly
                </button>
                <button
                  type="button"
                  className={`patternPill ${formData.recurringPattern === 'weekdays' ? 'isActive' : ''}`}
                  onClick={() => handlePatternChange('weekdays')}
                  aria-pressed={formData.recurringPattern === 'weekdays'}
                >
                  Weekdays
                </button>
                <button
                  type="button"
                  className={`patternPill ${formData.recurringPattern === 'custom' ? 'isActive' : ''}`}
                  onClick={() => handlePatternChange('custom')}
                  aria-pressed={formData.recurringPattern === 'custom'}
                >
                  Custom
                </button>
              </div>
            </div>

            {(formData.recurringPattern === 'weekly' || formData.recurringPattern === 'weekdays' || formData.recurringPattern === 'custom') && (
              <div className="form-group">
                <label>Days of Week</label>
                <div className="dow-row">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, index) => {
                    const isOn = (formData.recurringDaysOfWeek || []).includes(index);
                    const isLockedWeekdays = formData.recurringPattern === 'weekdays';
                    const isDisabled = isLockedWeekdays;
                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                    
                    return (
                      <button
                        key={index}
                        type="button"
                        className={`dowChip ${isOn ? 'isOn' : ''} ${isDisabled ? 'isDisabled' : ''}`}
                        onClick={() => !isDisabled && toggleDayOfWeek(index)}
                        disabled={isDisabled}
                        aria-pressed={isOn}
                        title={dayNames[index]}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="recurrenceSummary">
                  {formData.recurringPattern === 'weekdays'
                    ? 'Repeats Mon–Fri'
                    : (formData.recurringDaysOfWeek || []).length > 0
                      ? `Repeats on: ${(formData.recurringDaysOfWeek || [])
                          .map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d])
                          .join(', ')}`
                      : 'Select at least one day'}
                </p>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="recurringEndDate">End Date</label>
              <input
                type="date"
                id="recurringEndDate"
                name="recurringEndDate"
                value={formData.recurringEndDate}
                onChange={handleInputChange}
                min={formData.eventDate}
              />
            </div>
          </div>
        )}
      </div>

      {renderActions && (
        <div className={`form-actions ${compact ? 'sticky' : ''}`}>
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-create" disabled={loading}>
            {loading ? (
              <>
                <span className="btn-spinner"></span>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditMode ? '💾 Update Event' : '✨ Create Event'
            )}
          </button>
        </div>
      )}
    </form>
  );
}
